import * as React from "react";
import * as _ from "lodash";
import styled, { css } from "styled-components";

import { breakpoint } from "src/library/utils/styled-media-queries";
import Grid from "src/library/components/Hedron";
import { Text } from "src/library/components/Text";
import { IOrderLine } from "src/core/orders/api/orders.api";
import useCurrentUser from "src/core/users/hooks/useCurrentUser";
import Price from "src/core/prices/components/Price";

import { useCreditRequestContext } from "../context/CreditRequestContext";
import { useCreditReasonCodes } from "../hooks/useCreditReasonCodes";
import { CreditReasonSelect } from "./CreditReasonSelect";
import { CreditRequestItemReason } from "./CreditRequestItemReason";
import { CreditRowHeadersRight } from "./CreditRowHeaderRight";

interface CreditRequestItemProps {
  orderLine: IOrderLine;
  showApprovalColumn: boolean;
}

/**
 * Component to display one orderline
 * Within this row there may be multiple credit line lines (CreditRequestItemReason)
 */
export const CreditRequestOrderLine: React.ReactFCWithChildren<CreditRequestItemProps> = ({ orderLine, showApprovalColumn }) => {
  const creditReasons = useCreditReasonCodes();
  const [ { credits, mode }, { setCreditRequestRow, removeCreditRow } ] = useCreditRequestContext();
  const [ { currentRole } ] = useCurrentUser();

  // get all the credits associated with the selected orderline
  const creditsByLine = React.useMemo(() => {
    return _.filter(credits, c => c.orderLineId === orderLine.id);
  }, [ credits, orderLine.id ]);

  // Fileter all the already used credit reasons
  const availableCreditReasons = React.useMemo(() => {
    return _.filter(creditReasons, reason => !_.find(creditsByLine, cred => cred.creditRequestReasonCodeId === reason.id));
  }, [ creditReasons, creditsByLine ]);

  return (
    <CreditRequestOrderLineWrapper className="credit-row">
      <Grid.Bounds className="row-left">
        <div className="product-col credit-col">
          <Text
            as="h3"
            fontSize="h3"
          >
            {orderLine.product.description}
          </Text>

          <Text
            fontSize="sub"
            color="secondary"
          >
            {orderLine.product.partNumber}
          </Text>
        </div>

        {currentRole?.type === "admin" && (
          <div className="price-col credit-col">
            <Price
              type="unit"
              mode="credit"
              line={orderLine}
              product={orderLine.product}
              installedModules={[]}
            />
          </div>
        )}

        <div className="allowed-quantity-col credit-col">
          <Text>
            {orderLine.quantity}
          </Text>
        </div>
      </Grid.Bounds>

      {/* REASONS with quantiies here */}
      <Grid.Bounds
        direction="vertical"
        className="row-right"
      >
        <CreditRowHeadersRight showApprovalColumn={showApprovalColumn} />

        {_.map(creditsByLine, credit => (
          <CreditRequestItemReason
            key={`${credit.orderLineId}-${credit.creditRequestReasonCodeId}`}
            showApprovalColumn={showApprovalColumn}
            credit={credit}
            orderLine={orderLine}
            removeReason={() => {
              removeCreditRow(credit);
            }}
          />
        ))}

        {mode === "edit" && (
          <CreditReasonSelect
            orderLine={orderLine}
            availableCreditReasons={availableCreditReasons}
            showApprovalColumn={showApprovalColumn}
            onAddCreditReason={creditRequestReasonCodeId => setCreditRequestRow({
              orderLineId: orderLine.id as number,
              creditRequestReasonCodeId,
              quantityRequested: 0
            }, orderLine)}
          />
        )}

      </Grid.Bounds>

    </CreditRequestOrderLineWrapper>
  );
};

const CreditRequestOrderLineWrapper = styled(Grid.Bounds)`
  ${(({ theme }) => css`
    border-top: 1px solid ${theme.colors.lightGrey};

    .product-col, .price-col, .allowed-quantity-col {
      background: ${theme.colors.white};
    }

    ${breakpoint("md", theme)} {
      .row-right-heading  {
          display: none;
      }
    }
  `)}
`;
